export interface ServiceOption {
  id: string;
  label: string;
  description: string;
  priceLabel: string;
  basePrice: number;
  wasPrice?: string;
  popular?: boolean;
  icon: string;
  details?: string;
  similarTo?: string;
  maxLabour?: string;
  maxVolume?: string;
  maxWeight?: string;
}

export interface BookingItemData {
  id: string;
  label: string;
  unitPrice: number;
  icon: string;
  category: string;
  requiresPostcode?: boolean;
}

export type StepType = "load" | "items" | "rooms" | "schedule" | "bin";

export interface ServiceConfig {
  slug: string;
  title: string;
  priceFrom: string;
  image: string;
  stepType: StepType;
  options: ServiceOption[];
  itemsConfig?: BookingItemData[];
  minPrice: number;
  calloutCharge?: number;
}

export interface BookingItemData {
  id: string;
  label: string;
  unitPrice: number;
  icon: string;
  category: string;
  requiresPostcode?: boolean;
  note?: string;
}

export const bookingServices: ServiceConfig[] = [
  {
    slug: "rubbish-clearance",
    title: "Man & Van",
    priceFrom: "from £109",
    image: "https://readdy.ai/api/search-image?query=professional%20photograph%20of%20a%20clean%20white%20removal%20van%20parked%20on%20a%20bright%20residential%20street%20with%20a%20uniformed%20worker%20loading%20black%20bin%20bags%20and%20cardboard%20boxes%20into%20the%20open%20rear%20doors%20on%20a%20sunny%20day%2C%20sharp%20focus%2C%20wide%20angle%2C%20natural%20daylight%2C%20no%20text&width=600&height=400&seq=book-van-v31&orientation=landscape",
    stepType: "load",
    minPrice: 109,
    options: [
      {
        id: "flexible",
        label: "Flexible Load",
        description: "Only pay for what we actually collect",
        priceLabel: "£109",
        basePrice: 109,
        icon: "ri-scales-3-line",
        details: "Per 1 cubic yard / 0.76m³ · 75kg per unit",
        similarTo: "8 bin bags or a washing machine or 1 wheelie bin",
        maxLabour: "10 minutes",
        maxVolume: "1 yd³",
        maxWeight: "75kg",
      },
      {
        id: "mini",
        label: "Mini Load",
        description: "Up to 8 bin bags or a washing machine",
        priceLabel: "£109",
        basePrice: 109,
        icon: "ri-inbox-line",
        details: "1 cubic yard / 0.76m³ · 75kg limit",
        similarTo: "8 bin bags or a washing machine or 1 wheelie bin",
        maxLabour: "10 minutes",
        maxVolume: "1 yd³",
        maxWeight: "75kg",
      },
      {
        id: "small",
        label: "Small Load",
        description: "16 bin bags or 2 washing machines",
        priceLabel: "£155",
        basePrice: 155,
        icon: "ri-archive-2-line",
        details: "2 cubic yards / 1.53m³ · 150kg limit",
        similarTo: "16 bin bags or 2 washing machines or 2 wheelie bins",
        maxLabour: "20 minutes",
        maxVolume: "2 yd³",
        maxWeight: "150kg",
      },
      {
        id: "medium",
        label: "Medium Load",
        description: "30 bin bags or corner sofa",
        priceLabel: "£219",
        wasPrice: "£239",
        basePrice: 219,
        popular: true,
        icon: "ri-box-3-line",
        details: "4 cubic yards / 3.06m³ · 300kg limit",
        similarTo: "30 bin bags or corner sofa or 4 wheelie bins",
        maxLabour: "30 minutes",
        maxVolume: "4 yd³",
        maxWeight: "300kg",
      },
      {
        id: "large",
        label: "Large Load",
        description: "45 bin bags or corner sofa + 2 armchairs",
        priceLabel: "£299",
        basePrice: 299,
        icon: "ri-truck-line",
        details: "7 cubic yards / 5.35m³ · 525kg limit",
        similarTo: "45 bin bags or corner sofa + 2 armchairs or 7 wheelie bins",
        maxLabour: "50 minutes",
        maxVolume: "7 yd³",
        maxWeight: "525kg",
      },
      {
        id: "xlarge",
        label: "X-Large Load",
        description: "60 bin bags or half of a Luton Van",
        priceLabel: "£399",
        basePrice: 399,
        icon: "ri-truck-line",
        details: "10 cubic yards / 7.66m³ · 750kg limit",
        similarTo: "60 bin bags or half of a Luton Van or 10 wheelie bins",
        maxLabour: "70 minutes",
        maxVolume: "10 yd³",
        maxWeight: "750kg",
      },
      {
        id: "xxlarge",
        label: "XX-Large Load",
        description: "120 bin bags — a full load",
        priceLabel: "£459",
        wasPrice: "£499",
        basePrice: 459,
        icon: "ri-truck-line",
        details: "18 cubic yards / 13.76m³ · 1000kg limit",
        similarTo: "120 bin bags — a full load",
        maxLabour: "100 minutes",
        maxVolume: "18 yd³",
        maxWeight: "1,000kg",
      },
      {
        id: "monster",
        label: "Monster",
        description: "120 bin bags — a full load",
        priceLabel: "£599",
        wasPrice: "£699",
        basePrice: 599,
        icon: "ri-truck-line",
        details: "18 cubic yards / 13.76m³ · 1000kg limit",
        similarTo: "120 bin bags — a full load",
        maxLabour: "100 minutes",
        maxVolume: "18 yd³",
        maxWeight: "1,000kg",
      },
    ],
  },
  {
    slug: "rubbish-removal",
    title: "Rubbish Removal",
    priceFrom: "from £1.75",
    image: "https://readdy.ai/api/search-image?query=professional%20photograph%20of%20a%20residential%20rubbish%20removal%20job%20with%20neatly%20stacked%20black%20bin%20bags%20bulging%20cardboard%20boxes%20and%20mixed%20household%20waste%20items%20piled%20on%20a%20driveway%20ready%20for%20collection%2C%20bright%20natural%20daylight%2C%20sharp%20focus%2C%20wide%20angle%2C%20no%20text%20no%20people&width=600&height=400&seq=book-rubbish-v31&orientation=landscape",
    stepType: "items",
    minPrice: 1.75,
    options: [],
    itemsConfig: [
      { id: "rr-sofa-2", label: "2 Seater Sofa", unitPrice: 52.50, icon: "ri-sofa-line", category: "Sofas & Chairs" },
      { id: "rr-sofa-3", label: "3 Seater Sofa", unitPrice: 63.00, icon: "ri-sofa-line", category: "Sofas & Chairs" },
      { id: "rr-sofa-4", label: "4 Seater Sofa", unitPrice: 70.00, icon: "ri-sofa-line", category: "Sofas & Chairs" },
      { id: "rr-armchair", label: "Armchair", unitPrice: 28.00, icon: "ri-armchair-line", category: "Sofas & Chairs" },
      { id: "rr-chaise-lounge", label: "Chaise Lounge", unitPrice: 28.00, icon: "ri-sofa-line", category: "Sofas & Chairs" },
      { id: "rr-corner-sofa", label: "Corner Sofa", unitPrice: 77.00, icon: "ri-sofa-line", category: "Sofas & Chairs" },
      { id: "rr-wardrobe-2", label: "2 Door Wardrobe", unitPrice: 35.00, icon: "ri-door-open-line", category: "Wardrobes" },
      { id: "rr-wardrobe-3", label: "3 Door Wardrobe", unitPrice: 42.00, icon: "ri-door-open-line", category: "Wardrobes" },
      { id: "rr-bed-frame", label: "Bed Frame", unitPrice: 28.00, icon: "ri-hotel-bed-line", category: "Beds & Mattresses" },
      { id: "rr-mattress-single", label: "Single Mattress", unitPrice: 32.50, icon: "ri-hotel-bed-fill", category: "Beds & Mattresses" },
      { id: "rr-mattress-double", label: "Double / Queen Mattress", unitPrice: 43.00, icon: "ri-hotel-bed-fill", category: "Beds & Mattresses" },
      { id: "rr-mattress-king", label: "King Mattress", unitPrice: 45.00, icon: "ri-hotel-bed-fill", category: "Beds & Mattresses" },
      { id: "rr-chest-drawers", label: "Bedroom Chest of Drawers", unitPrice: 17.50, icon: "ri-inbox-2-line", category: "Bedroom" },
      { id: "rr-dining-table", label: "Dining Table", unitPrice: 24.50, icon: "ri-table-line", category: "Tables & Dining" },
      { id: "rr-coffee-table", label: "Coffee Table", unitPrice: 10.50, icon: "ri-table-line", category: "Tables & Dining" },
      { id: "rr-washing-machine", label: "Washing Machine", unitPrice: 28.00, icon: "ri-recycle-line", category: "Appliances" },
      { id: "rr-tumble-dryer", label: "Tumble Dryer", unitPrice: 21.00, icon: "ri-recycle-line", category: "Appliances" },
      { id: "rr-dishwasher", label: "Dishwasher", unitPrice: 24.50, icon: "ri-drop-line", category: "Appliances" },
      { id: "rr-fridge-freezer", label: "Fridge Freezer", unitPrice: 60.00, icon: "ri-temp-cold-line", category: "Appliances" },
      { id: "rr-cooker", label: "Cooker", unitPrice: 21.00, icon: "ri-fire-line", category: "Appliances" },
      { id: "rr-microwave", label: "Microwave", unitPrice: 7.00, icon: "ri-timer-line", category: "Appliances" },
      { id: "rr-tv", label: "TV / Monitor", unitPrice: 15.00, icon: "ri-tv-line", category: "Electronics" },
      { id: "rr-garden-dining-4", label: "4 Seat Garden Dining Set", unitPrice: 70.00, icon: "ri-plant-line", category: "Garden" },
      { id: "rr-garden-bench", label: "Garden Bench", unitPrice: 28.00, icon: "ri-plant-line", category: "Garden" },
      { id: "rr-lawnmower", label: "Lawnmower", unitPrice: 12.25, icon: "ri-scissors-line", category: "Garden" },
      { id: "rr-sink", label: "Bathroom Sink", unitPrice: 7.00, icon: "ri-water-flash-line", category: "Bathroom" },
      { id: "rr-bathtub", label: "Bathtub", unitPrice: 42.00, icon: "ri-water-flash-line", category: "Bathroom" },
      { id: "rr-toilet", label: "Toilet", unitPrice: 10.50, icon: "ri-water-flash-line", category: "Bathroom" },
      { id: "rr-office-desk", label: "Office Desk", unitPrice: 21.00, icon: "ri-computer-line", category: "Office" },
      { id: "rr-office-chair", label: "Study / Office Chair", unitPrice: 10.50, icon: "ri-layout-bottom-2-line", category: "Office" },
      { id: "rr-bags-heavy", label: "Heavy Duty Refuse Bags (90L) per bag", unitPrice: 7.50, icon: "ri-delete-bin-line", category: "Bags & Waste" },
      { id: "rr-bags-standard", label: "Standard Refuse Bags (60L) per bag", unitPrice: 5.00, icon: "ri-delete-bin-line", category: "Bags & Waste" },
    ],
  },
  {
    slug: "sofa-removal",
    title: "Sofa Collection",
    priceFrom: "from £52 + £55 callout",
    image: "https://readdy.ai/api/search-image?query=professional%20photograph%20of%20two%20removal%20workers%20carefully%20carrying%20a%20large%20grey%20fabric%20three-seater%20sofa%20out%20through%20the%20front%20door%20of%20a%20modern%20British%20semi-detached%20house%20on%20a%20sunny%20day%2C%20bright%20exterior%20lighting%2C%20sharp%20focus%2C%20wide%20angle%2C%20no%20text&width=600&height=400&seq=book-sofa-v31&orientation=landscape",
    stepType: "items",
    minPrice: 52,
    calloutCharge: 55,
    options: [],
    itemsConfig: [
      { id: "armchair-1", label: "1 Seat Armchair", unitPrice: 52, icon: "ri-armchair-line", category: "Sofas & Chairs", note: "Single armchair" },
      { id: "sofa-2", label: "2 Seat Sofa", unitPrice: 65, icon: "ri-sofa-line", category: "Sofas & Chairs", note: "Does not include sofa beds or recliners" },
      { id: "sofa-3", label: "3 Seat Sofa", unitPrice: 80, icon: "ri-sofa-line", category: "Sofas & Chairs", note: "This can include sofa beds or recliners" },
      { id: "sofa-4-corner", label: "4 Seat Sofa / Corner", unitPrice: 110, icon: "ri-sofa-line", category: "Sofas & Chairs", note: "4 seat sofa or corner sofa" },
    ],
  },
  {
    slug: "private-bin-collection",
    title: "Bin Collection",
    priceFrom: "from £25 + £55 callout",
    image: "https://readdy.ai/api/search-image?query=professional%20photograph%20of%20a%20neat%20row%20of%20grey%20and%20green%20residential%20wheelie%20bins%20lined%20up%20on%20a%20clean%20suburban%20driveway%20outside%20a%20British%20house%20on%20a%20bright%20morning%20ready%20for%20waste%20collection%2C%20sharp%20focus%2C%20wide%20angle%2C%20natural%20light%2C%20no%20text%20no%20people&width=600&height=400&seq=book-bins-v31&orientation=landscape",
    stepType: "bin",
    minPrice: 25,
    calloutCharge: 55,
    options: [],
    itemsConfig: [
      { id: "bin-120", label: "120L Bin", unitPrice: 25, icon: "ri-delete-bin-line", category: "Bin Sizes", requiresPostcode: true },
      { id: "bin-240", label: "240L Bin", unitPrice: 39, icon: "ri-delete-bin-2-line", category: "Bin Sizes", requiresPostcode: true },
      { id: "bin-360", label: "360L Bin", unitPrice: 43, icon: "ri-delete-bin-fill", category: "Bin Sizes", requiresPostcode: true },
      { id: "bin-660", label: "660L Bin", unitPrice: 49, icon: "ri-delete-bin-fill", category: "Bin Sizes", requiresPostcode: true },
      { id: "bin-1100", label: "1100L Bin", unitPrice: 69, icon: "ri-delete-bin-fill", category: "Bin Sizes", requiresPostcode: true },
    ],
  },
  {
    slug: "furniture-removal",
    title: "Furniture Collection",
    priceFrom: "from £73.49",
    image: "https://readdy.ai/api/search-image?query=professional%20photograph%20of%20an%20old%20wooden%20wardrobe%20a%20chest%20of%20drawers%20and%20a%20dining%20table%20stacked%20together%20on%20a%20residential%20driveway%20outside%20a%20British%20terraced%20home%20ready%20for%20furniture%20collection%2C%20bright%20natural%20daylight%2C%20sharp%20focus%2C%20wide%20angle%2C%20no%20text%20no%20people&width=600&height=400&seq=book-furn-v31&orientation=landscape",
    stepType: "items",
    minPrice: 73.49,
    options: [],
    itemsConfig: [
      { id: "wardrobe", label: "Wardrobe", unitPrice: 73.49, icon: "ri-door-open-line", category: "Bedroom" },
      { id: "bed-double", label: "Double Bed Frame", unitPrice: 73.49, icon: "ri-hotel-bed-line", category: "Bedroom" },
      { id: "mattress", label: "Mattress (any size)", unitPrice: 49.99, icon: "ri-hotel-bed-fill", category: "Bedroom" },
      { id: "dining-table", label: "Dining Table", unitPrice: 73.49, icon: "ri-table-line", category: "Living" },
      { id: "bookshelf", label: "Bookshelf / Shelving", unitPrice: 49.99, icon: "ri-archive-line", category: "Living" },
      { id: "chest-drawers", label: "Chest of Drawers", unitPrice: 59.99, icon: "ri-inbox-2-line", category: "Bedroom" },
      { id: "desk", label: "Desk", unitPrice: 59.99, icon: "ri-computer-line", category: "Office" },
      { id: "cabinet", label: "Cabinet / Sideboard", unitPrice: 59.99, icon: "ri-archive-2-line", category: "Living" },
    ],
  },
  {
    slug: "house-clearance",
    title: "House Clearance",
    priceFrom: "from £599",
    image: "https://readdy.ai/api/search-image?query=professional%20photograph%20of%20a%20full%20house%20clearance%20in%20progress%20with%20stacked%20cardboard%20boxes%20black%20bin%20bags%20old%20furniture%20and%20household%20items%20piled%20up%20on%20the%20front%20lawn%20of%20a%20traditional%20British%20red-brick%20terraced%20house%20under%20clear%20blue%20sky%2C%20sharp%20focus%2C%20wide%20angle%2C%20no%20text&width=600&height=400&seq=book-house-v31&orientation=landscape",
    stepType: "rooms",
    minPrice: 599,
    options: [
      {
        id: "hc-1bed",
        label: "1 Bedroom Property",
        description: "1 x 14 yd³ collection · 1 fridge/freezer · 1 mattress · 3 upholstered items · 1 TV",
        priceLabel: "£599",
        basePrice: 599,
        icon: "ri-home-2-line",
        details: "2 hours labour included · was £699",
      },
      {
        id: "hc-2bed",
        label: "2 Bedroom Property",
        description: "2 x 14 yd³ collections · 1 fridge/freezer · 2 mattresses · 4 upholstered items · 1 TV",
        priceLabel: "£1,099",
        basePrice: 1099,
        icon: "ri-home-3-line",
        details: "4 hours labour included · was £1,188",
      },
      {
        id: "hc-3bed",
        label: "3 Bedroom Property",
        description: "3 x 18 yd³ collections · 1 fridge/freezer · 3 mattresses · 4 upholstered items · 1 TV",
        priceLabel: "£1,799",
        basePrice: 1799,
        popular: true,
        icon: "ri-building-2-line",
        details: "6 hours labour included · was £1,840",
      },
      {
        id: "hc-4bed",
        label: "4 Bedroom Property",
        description: "3 x 18 yd³ collections · 1 fridge/freezer · 4 mattresses · 5 upholstered items · 1 TV",
        priceLabel: "£2,099",
        basePrice: 2099,
        icon: "ri-building-3-line",
        details: "8 hours labour included · was £2,177",
      },
      {
        id: "hc-5bed",
        label: "5 Bedroom Property",
        description: "4 x 18 yd³ collections · 1 fridge/freezer · 5 mattresses · 5 upholstered items · 1 TV",
        priceLabel: "£2,399",
        basePrice: 2399,
        icon: "ri-building-4-line",
        details: "10 hours labour included · was £2,460",
      },
    ],
  },
  {
    slug: "appliance-removal",
    title: "Appliance Removal",
    priceFrom: "from £2.00",
    image: "https://readdy.ai/api/search-image?query=professional%20photograph%20of%20a%20uniformed%20removal%20worker%20carefully%20wheeling%20a%20large%20white%20fridge-freezer%20on%20a%20sack%20truck%20through%20the%20hallway%20towards%20the%20front%20door%20of%20a%20clean%20modern%20domestic%20house%2C%20bright%20interior%20lighting%2C%20sharp%20focus%2C%20wide%20angle%2C%20no%20text&width=600&height=400&seq=book-appl-v31&orientation=landscape",
    stepType: "items",
    minPrice: 2.00,
    options: [],
    itemsConfig: [
      { id: "appl-air-con", label: "Air Con Unit", unitPrice: 45.00, icon: "ri-temp-cold-line", category: "Heating & Cooling" },
      { id: "appl-air-fryer", label: "Air Fryer", unitPrice: 4.00, icon: "ri-fire-line", category: "Kitchen" },
      { id: "appl-fridge-american", label: "American / Double Fridge Freezer", unitPrice: 105.00, icon: "ri-temp-cold-line", category: "Kitchen" },
      { id: "appl-boiler", label: "Boiler", unitPrice: 42.00, icon: "ri-fire-line", category: "Heating & Cooling" },
      { id: "appl-chainsaw", label: "Chainsaw", unitPrice: 8.00, icon: "ri-scissors-cut-line", category: "Garden" },
      { id: "appl-coffee-machine", label: "Coffee Machine", unitPrice: 4.00, icon: "ri-cup-line", category: "Kitchen" },
      { id: "appl-dishwasher", label: "Dishwasher", unitPrice: 26.00, icon: "ri-drop-line", category: "Kitchen" },
      { id: "appl-exercise-bike", label: "Exercise Bike", unitPrice: 18.50, icon: "ri-riding-line", category: "Fitness" },
      { id: "appl-extractor-hood", label: "Extractor Hood", unitPrice: 7.50, icon: "ri-filter-line", category: "Kitchen" },
      { id: "appl-fax-machine", label: "Fax Machine", unitPrice: 6.00, icon: "ri-printer-line", category: "Office" },
      { id: "appl-fridge-freezer", label: "Fridge Freezer", unitPrice: 63.00, icon: "ri-temp-cold-line", category: "Kitchen" },
      { id: "appl-hob", label: "Hob", unitPrice: 7.50, icon: "ri-fire-line", category: "Kitchen" },
      { id: "appl-kettle", label: "Kettle", unitPrice: 2.00, icon: "ri-cup-line", category: "Kitchen" },
      { id: "appl-large-printer", label: "Large Printer / Photocopier", unitPrice: 20.50, icon: "ri-printer-line", category: "Office" },
      { id: "appl-lawnmower", label: "Lawnmower", unitPrice: 13.00, icon: "ri-scissors-line", category: "Garden" },
      { id: "appl-microwave", label: "Microwave", unitPrice: 7.50, icon: "ri-timer-line", category: "Kitchen" },
      { id: "appl-office-printer", label: "Office Desk Printer", unitPrice: 6.00, icon: "ri-printer-line", category: "Office" },
      { id: "appl-oven-cooker", label: "Oven Cooker", unitPrice: 22.50, icon: "ri-fire-line", category: "Kitchen" },
      { id: "appl-storage-tank", label: "Storage Tank / Cylinder", unitPrice: 42.00, icon: "ri-database-line", category: "Heating & Cooling" },
      { id: "appl-toaster", label: "Toaster", unitPrice: 2.00, icon: "ri-bread-line", category: "Kitchen" },
      { id: "appl-treadmill", label: "Treadmill", unitPrice: 37.00, icon: "ri-run-line", category: "Fitness" },
      { id: "appl-tumble-dryer", label: "Tumble Dryer", unitPrice: 22.50, icon: "ri-recycle-line", category: "Laundry" },
      { id: "appl-tv-monitor", label: "TV / Monitor", unitPrice: 16.00, icon: "ri-tv-line", category: "Electronics" },
      { id: "appl-fridge-small", label: "Under Counter (Small) Fridge", unitPrice: 45.00, icon: "ri-temp-cold-line", category: "Kitchen" },
      { id: "appl-washing-machine", label: "Washing Machine", unitPrice: 29.50, icon: "ri-recycle-line", category: "Laundry" },
    ],
  },
];

export const timeSlots = [
  { id: "anytime", label: "Anytime", sublabel: "07:30 - 18:00", price: 0, icon: "ri-24-hours-line" },
  { id: "morning", label: "Morning", sublabel: "07:30 - 13:00", price: 15, icon: "ri-sun-foggy-line" },
  { id: "afternoon", label: "Afternoon", sublabel: "13:00 - 18:00", price: 15, icon: "ri-sun-line" },
  { id: "4hour", label: "4 Hour Window", sublabel: "Flexible 4hr slot", price: 30, icon: "ri-timer-line" },
];

export interface SurchargeItem {
  id: string;
  label: string;
  unitPrice: number;
  icon: string;
}

export const surchargeItems: SurchargeItem[] = [
  { id: "surcharge-small-fridge", label: "Small Fridge / Fridge", unitPrice: 25, icon: "ri-temp-cold-line" },
  { id: "surcharge-large-fridge", label: "Large Fridge / American Style", unitPrice: 40, icon: "ri-temp-cold-line" },
  { id: "surcharge-mattress", label: "Mattress (any size)", unitPrice: 15, icon: "ri-hotel-bed-fill" },
  { id: "surcharge-tv", label: "TV / Monitor", unitPrice: 15, icon: "ri-tv-2-line" },
  { id: "surcharge-washing-machine", label: "Washing Machine / Dryer", unitPrice: 25, icon: "ri-recycle-line" },
  { id: "surcharge-tyre", label: "Tyre", unitPrice: 20, icon: "ri-steering-2-line" },
];

export const cannotTakeItems: string[] = [
  "Asbestos",
  "Paint or paint cans (unless empty)",
  "Liquid chemicals or solvents",
  "Car batteries",
  "Fire extinguishers",
  "Gas bottles",
  "Oils or petrol",
  "Fluorescent lamps / tubes",
];

export const binProhibitedItems: string[] = [
  "Asbestos",
  "Paint or paint cans (unless empty)",
  "Liquid chemicals or solvents",
  "Car batteries",
  "Raw meat or meat (unless in sealed packaging)",
  "Clinical or biological waste",
  "Fire extinguishers",
  "Gas bottles",
  "Oils or petrol",
  "Fluorescent lamps / tubes",
];
